import { Link, useParams } from "react-router-dom";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { getArticleById } from "@/mocks/articles";
import { siteName } from "@/mocks/site";
import ArticleHeader from "./components/ArticleHeader";
import ArticleBody from "./components/ArticleBody";
import ArticleTimeline from "./components/ArticleTimeline";
import ArticleEvidence from "./components/ArticleEvidence";
import ArticleAside from "./components/ArticleAside";
import ArticleRelated from "./components/ArticleRelated";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-search-line"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已经被合并到更新的深扒稿件里，或者链接不太对。你可以回到首页继续浏览最新爆料。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = article.related
    .map((rid) => getArticleById(rid))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: siteName },
        publisher: { "@type": "Organization", name: siteName },
        articleSection: article.category,
        keywords: article.tags.join(","),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://billionan.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: article.category,
            item: `https://billionan.cn/?cat=${article.categorySlug}`,
          },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <title>{`${article.title} | ${siteName}`}</title>
      <meta name="description" content={article.excerpt.slice(0, 150)} />
      <meta name="keywords" content={article.tags.join(",")} />
      <link rel="canonical" href={`https://billionan.cn/article/${article.id}`} />
      <meta name="robots" content="index,follow,max-image-preview:large" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt.slice(0, 150)} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <SiteHeader />
      <main>
        <div className="mx-auto max-w-content px-4 pt-6 md:px-6">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: article.category, to: `/?cat=${article.categorySlug}#latest` },
              { label: article.title },
            ]}
          />
        </div>

        <ArticleHeader article={article} />

        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <article className="lg:col-span-8">
              <ArticleBody article={article} />
              <ArticleTimeline article={article} />
              <ArticleEvidence article={article} />
              <p className="mt-8 rounded-md border border-background-300 bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                声明：本文内容基于公开信息与行业通行惯例整理，涉及人物均作匿名化处理，不构成对任何自然人、机构的定性结论。如有异议，欢迎通过投稿邮箱与我们联系。
              </p>
            </article>
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <ArticleAside article={article} />
              </div>
            </div>
          </div>

          <ArticleRelated items={related} />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}